import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { MeetingFormat } from '../../core/models/meeting-format';
import {
    FORMATTED_LENGTH,
    KAZAKHSTAN_DIAL_CODE,
    formatSubscriberDigits,
    toSubscriberDigits
} from '../../core/phone/kazakhstan-phone';
import { BookingSummary } from './booking-summary';

export type ContactField = 'firstName' | 'lastName' | 'phone' | 'email' | 'company';

interface ContactStepProps {
    serviceName: string;
    specialistName: string | null;
    format: MeetingFormat;
    date: string | null;
    time: string | null;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    /** The honeypot's value. Never shown, never validated, never acted on by this component. */
    company: string;
    /** Whether the visitor has agreed to their personal data being processed. */
    consent: boolean;
    /**
     * Set when a customer is signed in: the fields show their account and cannot be edited, because
     * the server takes the details from the account whatever the page sends. Booking for somebody
     * else is signing out first.
     */
    account: { loading: boolean; onSignOut: () => void } | null;
    errors: Record<string, TranslationKey>;
    onChange: (field: ContactField, value: string) => void;
    onConsentChange: (value: boolean) => void;
    /** The wizard's error banner and button row, which the live page draws inside the card. */
    children: ReactNode;
}

/**
 * The last step: who to call.
 *
 * A real `<form>` with real `autoComplete`, which the live page has neither of — it has no `form`
 * element at all, so no submit event, no Enter key and no autofill for a customer filling four
 * fields by hand on a telephone.
 *
 * **The phone box collects ten digits under a fixed `+7`.** The country code is an addon rather
 * than something to type, so there is no state in which the box holds a number belonging to
 * somewhere else — which is what lets the wizard say a number is short while it is being typed
 * instead of after a round trip. The server normalises and decides either way.
 */
export function ContactStep({
    serviceName,
    specialistName,
    format,
    date,
    time,
    firstName,
    lastName,
    phone,
    email,
    company,
    consent,
    account,
    errors,
    onChange,
    onConsentChange,
    children
}: ContactStepProps) {
    const { t } = useTranslation();
    const language = useLanguage();
    const consentError = errors['consentVersion'];
    const consentErrorId = useId();

    return (
        <div className="card">
            <span className="card-lbl">{t('booking.s3.step')}</span>
            <h1 className="card-title">{t('booking.s3.title')}</h1>

            <BookingSummary
                serviceName={serviceName}
                specialistName={specialistName}
                format={format}
                date={date}
                time={time}
                variant="bar"
            />

            {account !== null && (
                <div className="account-note" role="status">
                    <p className="account-note-title">
                        {account.loading ? t('booking.s3.profile_loading') : t('booking.s3.signed_in')}
                    </p>
                    <p>
                        {t('booking.s3.other_person')}{' '}
                        <button type="button" onClick={account.onSignOut}>
                            {t('nav.sign_out')}
                        </button>
                    </p>
                </div>
            )}

            <div className="s3g">
                <Field
                    readOnly={account !== null}
                    label={t('booking.first_name')}
                    required
                    value={firstName}
                    autoComplete="given-name"
                    placeholder={t('booking.s3.name_ph')}
                    error={errors['customerName']}
                    onChange={(value) => onChange('firstName', value)}
                />
                <Field
                    readOnly={account !== null}
                    label={t('booking.last_name')}
                    value={lastName}
                    autoComplete="family-name"
                    placeholder={t('booking.s3.sname_ph')}
                    error={errors['customerLastName']}
                    onChange={(value) => onChange('lastName', value)}
                />
                <Field
                    className="full"
                    readOnly={account !== null}
                    label={t('booking.phone')}
                    required
                    value={formatSubscriberDigits(phone)}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    placeholder={t('booking.s3.phone_ph')}
                    prefix={`🇰🇿 ${KAZAKHSTAN_DIAL_CODE}`}
                    maxLength={FORMATTED_LENGTH}
                    error={errors['customerPhone']}
                    onChange={(value) => onChange('phone', toSubscriberDigits(value))}
                />
                <Field
                    className="full"
                    readOnly={account !== null}
                    label={t('booking.email')}
                    value={email}
                    type="email"
                    autoComplete="email"
                    error={errors['customerEmail']}
                    onChange={(value) => onChange('email', value)}
                />

                {/*
                  Consent to personal-data processing, which is what makes collecting the fields
                  above lawful and which the live form asks for nowhere. Unticked by default and
                  required — a pre-ticked box is not consent, and the submit button stays enabled so
                  pressing it explains what is missing rather than leaving a dead control.

                  The wording is a translation key rather than a fetched string, because the version
                  sent with the submission has to be the version of the text that was actually on
                  screen. See `PERSONAL_DATA_CONSENT_VERSION`.
                */}
                <label className="consent">
                    <input
                        type="checkbox"
                        checked={consent}
                        aria-invalid={consentError !== undefined}
                        aria-describedby={consentError === undefined ? undefined : consentErrorId}
                        onChange={(event) => onConsentChange(event.target.checked)}
                    />
                    <span>
                        {t('booking.consent')}{' '}
                        <Link to={`/${language}/policy`} target="_blank" rel="noopener">
                            {t('booking.consent_link')}
                        </Link>
                        {consentError !== undefined && (
                            <span id={consentErrorId} role="alert" className="fe">
                                {t(consentError)}
                            </span>
                        )}
                    </span>
                </label>
            </div>

            {/*
              The honeypot. Off-screen rather than `display: none`, hidden from assistive
              technology, out of the tab order and with autofill switched off — `website`,
              `address` and `company` are exactly what a password manager fills in *for the
              customer*, which would turn a bot trap into a rejection of real submissions. The
              endpoint answers a tripped honeypot like a success, so nothing here branches on it.
            */}
            {account === null && (
                <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
                    <label htmlFor="company">Company</label>
                    <input
                        id="company"
                        name="company"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={company}
                        onChange={(event) => onChange('company', event.target.value)}
                    />
                </div>
            )}

            {children}
        </div>
    );
}

/**
 * One labelled field, with its error **announced** rather than merely displayed.
 *
 * `aria-invalid` alone says a field is wrong and not why. The message is linked with
 * `aria-describedby` so it becomes part of what the field reads out, and carries `role="alert"` so
 * it is announced when it appears — a customer who pressed submit and heard nothing has no way to
 * discover which of five boxes the server objected to.
 *
 * The `prefix` is the country box on the phone field. It is welded to the input's left edge and is
 * never part of what the box holds: the customer types the ten digits after it, and the caller adds
 * the code back on submission. That is why it carries `aria-hidden` — the label already says the
 * field is a telephone number, and a screen reader announcing a flag between the label and the
 * value would be reading out decoration.
 */
function Field({
    label,
    required = false,
    readOnly = false,
    value,
    type = 'text',
    inputMode,
    autoComplete,
    placeholder,
    prefix,
    maxLength,
    className,
    error,
    onChange
}: {
    label: string;
    required?: boolean;
    readOnly?: boolean;
    value: string;
    type?: string;
    inputMode?: 'text' | 'email' | 'tel' | 'numeric';
    autoComplete?: string;
    placeholder?: string;
    prefix?: string;
    maxLength?: number;
    className?: string;
    error?: TranslationKey;
    onChange: (value: string) => void;
}) {
    const { t } = useTranslation();
    const fieldId = useId();
    const errorId = useId();

    const input = (
        <input
            id={fieldId}
            className={error === undefined ? 'fi' : 'fi err'}
            type={type}
            value={value}
            inputMode={inputMode}
            autoComplete={autoComplete}
            placeholder={placeholder}
            maxLength={maxLength}
            readOnly={readOnly}
            aria-readonly={readOnly}
            aria-invalid={error !== undefined}
            aria-describedby={error === undefined ? undefined : errorId}
            onChange={(event) => onChange(event.target.value)}
        />
    );

    return (
        <div className={className}>
            <label className="fl" htmlFor={fieldId}>
                <span>{label}</span>
                {required && <span className="req">*</span>}
            </label>

            {prefix === undefined ? (
                input
            ) : (
                <div className="ph-row">
                    <span className="ph-pfx" aria-hidden="true">
                        {prefix}
                    </span>
                    {input}
                </div>
            )}

            {error !== undefined && (
                <p className="fe" id={errorId} role="alert">
                    {t(error)}
                </p>
            )}
        </div>
    );
}
